/**
 * Carrito lateral de Ritualic
 *
 * La mecanica viene del carrito de Marawi (`mw-cart-drawer.liquid`): se abre al
 * agregar, pinta desde `/cart.js`, cambia cantidades contra `/cart/change.js` y
 * lleva una barra de progreso hacia el envio gratis. Lo que cambia aqui es de
 * donde le llega el aviso y como se ve.
 *
 * Marawi escuchaba un evento propio, `cart:refresh`, porque su boton de agregar
 * era suyo. Aqui el boton es el de Horizon, que despacha en `document` el evento
 * estandar de Shopify `shopify:cart:lines-update` con `action: 'add'`. Verificado
 * en la tienda interceptando `dispatchEvent`.
 *
 * El cajon propio de Horizon sigue montado pero con el auto-abrir apagado, asi
 * que no compiten: el suyo solo aparece si alguien lo abre a mano.
 */
/**
 * El contador del header lo lleva `cart-icon.js` de Horizon, que escucha el
 * evento estandar de Shopify pero espera su clase de evento, no un CustomEvent
 * cualquiera. En vez de imitar esa forma interna, se escribe directamente en la
 * burbuja: es un numero y una clase, y no depende de detalles del tema que
 * pueden cambiar en la proxima version.
 */
function ritEscribirBurbuja(cantidad) {
  document.querySelectorAll('.cart-bubble').forEach((burbuja) => {
    burbuja.classList.toggle('visually-hidden', cantidad === 0);
    const cifra = burbuja.querySelector('[ref="cartBubbleCount"]');
    if (!cifra) return;
    cifra.classList.toggle('hidden', cantidad === 0);
    cifra.textContent = cantidad < 100 ? String(cantidad) : '';
  });
}

/**
 * El contador de Horizon tambien corre su propia carrera: reacciona al evento
 * optimista y vuelve a pintar el header con el carrito viejo, a veces DESPUES de
 * que nosotros hayamos escrito el valor bueno. Medido: con un articulo en el
 * carrito la burbuja se quedaba en 0.
 *
 * Se escribe tres veces —ahora, en el siguiente fotograma y medio segundo
 * despues— para caer tambien del otro lado de su repintado. Se vuelve a buscar
 * el elemento en cada pasada porque Horizon lo reemplaza, no lo edita.
 */
function ritActualizarBurbuja(cantidad) {
  ritEscribirBurbuja(cantidad);
  requestAnimationFrame(() => ritEscribirBurbuja(cantidad));
  setTimeout(() => ritEscribirBurbuja(cantidad), 500);
}

class RitCartDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-rit-cd="panel"]');
    this.velo = this.querySelector('[data-rit-cd="velo"]');
    this.cuerpo = this.querySelector('[data-rit-cd="cuerpo"]');
    this.pie = this.querySelector('[data-rit-cd="pie"]');
    this.subtotal = this.querySelector('[data-rit-cd="subtotal"]');
    this.envioTexto = this.querySelector('[data-rit-cd="envio-texto"]');
    this.envioRelleno = this.querySelector('[data-rit-cd="envio-relleno"]');
    this.vacio = this.querySelector('[data-rit-cd="vacio"]')?.outerHTML ?? '';
    this.umbral = Number(this.dataset.umbral || 0);

    this.querySelector('[data-rit-cd="cerrar"]')?.addEventListener('click', () => this.cerrar());
    this.velo?.addEventListener('click', () => this.cerrar());
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.cerrar();
    });

    // El boton de agregar es el de Horizon; el aviso es el evento estandar.
    //
    // OJO con la carrera: Horizon despacha el evento de forma optimista, ANTES
    // de que termine el POST a `/cart/add.js`. Medido en la tienda: el evento
    // llega en t=67580 y el POST no resuelve hasta t=68470. Pidiendo `/cart.js`
    // al oir el evento se recibia el carrito viejo, y el cajon se abria vacio
    // con el producto ya agregado. Un carrito que dice estar vacio justo despues
    // de agregar no es un fallo visual, es una razon para no comprar.
    //
    // El evento trae la operacion en curso en `e.promise`, que resuelve con el
    // carrito ya actualizado dentro. Comprobado: sin esperarla el carrito
    // devuelve 3 articulos, esperandola devuelve 4.
    document.addEventListener('shopify:cart:lines-update', (e) => {
      if (e.action !== 'add' && e.detail?.action !== 'add') return;
      this.alAgregar(e);
    });

    // El icono del header y cualquier otro disparador propio.
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-rit-cd-abrir]')) {
        e.preventDefault();
        this.traer(true);
      }
    });

    this.traer(false);
  }

  /**
   * Se abre de inmediato para que el clic tenga respuesta, pero en estado de
   * carga: sin eso el cliente ve un carrito vacio durante el segundo y medio
   * que tarda el servidor, que es justo lo que rompia la confianza.
   */
  async alAgregar(evento) {
    this.abrir();
    this.cargando(true);
    try {
      // La promesa sirve para el tiempo, no para los datos. El carrito que trae
      // dentro viene con la forma de la API de Storefront —`totalQuantity`,
      // `cost`, `lines`— y no con la de `/cart.js`, que es la que este cajon
      // pinta: `item_count`, `items`, `total_price`. Pasarselo tal cual dejaba
      // `items` en undefined y el cajon se pintaba vacio con el carrito lleno.
      //
      // Asi que se espera la operacion y despues se pide el carrito en su forma
      // correcta. Medido: antes de esperar devuelve 1 articulo, despues 2.
      await evento.promise;
      const carrito = await (await fetch('/cart.js', { cache: 'no-store' })).json();
      this.pintar(carrito);
    } catch {
      // Si la promesa no viene o falla, se cae al camino de siempre.
      await this.traer(false);
    } finally {
      this.cargando(false);
    }
  }

  cargando(si) {
    this.classList.toggle('esta-cargando', si);
  }

  abrir() {
    this.panel?.classList.add('is-open');
    this.velo?.classList.add('is-open');
    document.body.classList.add('rit-cd-abierto');
    this.querySelector('[data-rit-cd="cerrar"]')?.focus();
  }

  cerrar() {
    this.panel?.classList.remove('is-open');
    this.velo?.classList.remove('is-open');
    document.body.classList.remove('rit-cd-abierto');
  }

  /**
   * El precio se arma con `shop.money_format`, el mismo ajuste que usa el filtro
   * `money` de Liquid en el resto del tema. Intl no sirve aqui: el documento
   * declara `lang="es"` a secas y con ese locale un peso colombiano sale como
   * "195.000 COP", mientras la ficha muestra "$195.000". Dos formatos distintos
   * para el mismo precio en la misma pantalla.
   */
  dinero(centavos, moneda) {
    const formato = this.dataset.formatoMoneda || '${{amount_no_decimals_with_comma_separator}}';
    const valor = centavos / 100;

    const conSeparadores = (n, decimales, miles, decimal) => {
      const fijo = n.toFixed(decimales);
      const [entero, resto] = fijo.split('.');
      const agrupado = entero.replace(/\B(?=(\d{3})+(?!\d))/g, miles);
      return resto ? agrupado + decimal + resto : agrupado;
    };

    const piezas = {
      amount: () => conSeparadores(valor, 2, ',', '.'),
      amount_no_decimals: () => conSeparadores(valor, 0, ',', '.'),
      amount_with_comma_separator: () => conSeparadores(valor, 2, '.', ','),
      amount_no_decimals_with_comma_separator: () => conSeparadores(valor, 0, '.', ','),
      amount_with_space_separator: () => conSeparadores(valor, 2, ' ', ','),
      amount_no_decimals_with_space_separator: () => conSeparadores(valor, 0, ' ', ','),
      amount_with_apostrophe_separator: () => conSeparadores(valor, 2, "'", '.'),
    };

    const texto = formato.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, clave) =>
      piezas[clave] ? piezas[clave]() : conSeparadores(valor, 0, '.', ',')
    );
    return texto === formato ? `$${conSeparadores(valor, 0, '.', ',')}` : texto;
  }

  pintar(carrito) {
    const articulos = carrito.items || [];
    const moneda = carrito.currency || 'COP';

    if (this.umbral > 0 && this.envioRelleno) {
      const falta = this.umbral - carrito.total_price;
      const pct = Math.min(100, (carrito.total_price / this.umbral) * 100);
      this.envioRelleno.style.width = `${pct}%`;
      if (this.envioTexto) {
        this.envioTexto.innerHTML =
          falta <= 0
            ? this.dataset.envioLogrado
            : this.dataset.envioFalta.replace('[[falta]]', `<strong>${this.dinero(falta, moneda)}</strong>`);
      }
    }

    if (!articulos.length) {
      this.cuerpo.innerHTML = this.vacio;
      this.pie.hidden = true;
      return;
    }

    this.cuerpo.innerHTML = articulos.map((a) => this.articulo(a, moneda)).join('');
    this.subtotal.textContent = this.dinero(carrito.total_price, moneda);
    this.pie.hidden = false;

    this.cuerpo.querySelectorAll('[data-paso]').forEach((b) => {
      b.addEventListener('click', () => {
        const nueva = Number(b.dataset.cantidad) + Number(b.dataset.paso);
        this.cambiar(b.dataset.clave, Math.max(0, nueva));
      });
    });
    this.cuerpo.querySelectorAll('[data-quitar]').forEach((b) => {
      b.addEventListener('click', () => this.cambiar(b.dataset.quitar, 0));
    });

    // El contador del header va aqui, al final de `pintar`, que es el unico
    // sitio por el que pasan todos los caminos: agregar, cambiar cantidad y
    // quitar acaban los tres en este metodo. Estuvo por error dentro de
    // `cambiar`, donde `carrito` ni siquiera existe: por eso el contador no se
    // movia al agregar y cambiar cantidad lanzaba un error.
    ritActualizarBurbuja(carrito.item_count);
  }

  articulo(a, moneda) {
    const esc = (t) => String(t ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
    const variante = a.variant_title && a.variant_title !== 'Default Title'
      ? `<p class="rit-cd__variante">${esc(a.variant_title)}</p>`
      : '';
    const foto = a.image
      ? `<img src="${esc(a.image)}" alt="${esc(a.product_title)}" loading="lazy" width="72" height="72">`
      : '';
    return `
      <li class="rit-cd__linea">
        <a class="rit-cd__foto" href="${esc(a.url)}">${foto}</a>
        <div class="rit-cd__datos">
          <a class="rit-cd__nombre" href="${esc(a.url)}">${esc(a.product_title)}</a>
          ${variante}
          <p class="rit-cd__precio">${this.dinero(a.final_line_price, moneda)}</p>
          <div class="rit-cd__acciones">
            <div class="rit-cd__pasos">
              <button type="button" class="rit-cd__paso" data-paso="-1" data-cantidad="${a.quantity}" data-clave="${esc(a.key)}" aria-label="${esc(this.dataset.menos)}">&minus;</button>
              <span class="rit-cd__cantidad">${a.quantity}</span>
              <button type="button" class="rit-cd__paso" data-paso="1" data-cantidad="${a.quantity}" data-clave="${esc(a.key)}" aria-label="${esc(this.dataset.mas)}">+</button>
            </div>
            <button type="button" class="rit-cd__quitar" data-quitar="${esc(a.key)}">${esc(this.dataset.quitar)}</button>
          </div>
        </div>
      </li>`;
  }

  async cambiar(clave, cantidad) {
    const r = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: clave, quantity: cantidad }),
    });
    this.pintar(await r.json());
    // Aqui NO se vuelve a despachar `shopify:cart:lines-update`. El contador de
    // Horizon escucha ese evento y suma por su cuenta a partir de las lineas que
    // trae, asi que un evento nuestro encima del valor que acabamos de escribir
    // lo hacia contar dos veces: el carrito tenia 1 articulo y la burbuja decia
    // 2. Un contador que miente en el header es el mismo problema de confianza
    // que el cajon vacio, mas pequeno.
  }

  async traer(abrirDespues) {
    const r = await fetch('/cart.js');
    this.pintar(await r.json());
    if (abrirDespues) this.abrir();
  }
}

if (!customElements.get('rit-cart-drawer')) customElements.define('rit-cart-drawer', RitCartDrawer);
